import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authStore'
import { toast } from 'sonner'

const API_URL = import.meta.env.VITE_API_URL

export const api = axios.create({
  baseURL: `${API_URL}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Track refresh in-flight to avoid concurrent refresh calls
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken
  if (!refreshToken) return false

  try {
    const { data } = await axios.post(`${API_URL}/admin/auth/refresh`, {
      refresh_token: refreshToken,
    })
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
    return true
  } catch {
    return false
  }
}

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status
    const message =
      (error.response?.data as { detail?: string; message?: string })?.detail ||
      (error.response?.data as { detail?: string; message?: string })?.message ||
      'Error inesperado'

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    switch (status) {
      case 401: {
        if (originalRequest && !originalRequest._retry) {
          originalRequest._retry = true

          if (!isRefreshing) {
            isRefreshing = true
            refreshPromise = tryRefreshToken()
          }

          const success = await refreshPromise
          isRefreshing = false
          refreshPromise = null

          if (success) {
            const newToken = useAuthStore.getState().token
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return api(originalRequest)
          }
        }

        useAuthStore.getState().logout()
        window.location.href = '/login'
        toast.error('Sesion expirada. Por favor inicia sesion nuevamente.')
        break
      }
      case 403:
        toast.error('No tienes permisos para realizar esta accion.')
        break
      case 404:
        toast.error('Recurso no encontrado.')
        break
      case 422:
        toast.error(`Error de validacion: ${message}`)
        break
      case 500:
        toast.error('Error interno del servidor. Intentalo mas tarde.')
        break
      default:
        toast.error(message)
    }

    return Promise.reject(error)
  }
)
